import React, {useEffect, useRef, useState} from 'react';
import {Modal} from 'react-bootstrap';
import {customerApi, getOtpCooldown} from '../../api/Api';
import {TOKEN_KEY, USERNAME_KEY} from '../../utils/session';
import BotanicalLeaf from '../utils/BotanicalLeaf';
import './ProfileCompletionNotice.css';

function FieldIcon({email = false}) {
    return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true" focusable="false">
        {email ? <><rect x="3" y="5" width="18" height="14" rx="3"/><path d="m4 7 8 6 8-6"/></> : <><circle cx="12" cy="8" r="3.5"/><path d="M5 21v-2a7 7 0 0 1 14 0v2"/></>}
    </svg>;
}

export default function ProfileCompletionModal({profile, token, onLater, onSaved}) {
    const customer = profile.customer;
    const missing = new Set(profile.profile_completion.missing_fields.map(item => item.field));
    const needsName = missing.has('first_name');
    const needsEmail = missing.has('email') || missing.has('email_verified');
    const [fullName, setFullName] = useState([customer.first_name, customer.last_name].filter(Boolean).join(' '));
    const [email, setEmail] = useState(customer.email || '');
    const [requestId, setRequestId] = useState('');
    const [otp, setOtp] = useState('');
    const [emailVerified, setEmailVerified] = useState(false);
    const [cooldown, setCooldown] = useState(0);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const pending = useRef(false);
    const mounted = useRef(true);
    useEffect(() => {
        mounted.current = true;
        return () => {mounted.current = false;};
    }, []);
    useEffect(() => {
        if (!cooldown) return;
        const timer = setTimeout(() => setCooldown(value => Math.max(0, value - 1)), 1000);
        return () => clearTimeout(timer);
    }, [cooldown]);
    const current = () => mounted.current && localStorage.getItem(TOKEN_KEY) === token;

    const run = async action => {
        if (pending.current || !current()) return;
        pending.current = true;
        setBusy(true);
        setError('');
        try {
            await action();
        } catch (failure) {
            if (!current()) return;
            const data = failure.response?.data;
            if (data?.code === 'EMAIL_OTP_DELIVERY_DELAYED' && data.data?.request_id) {
                setRequestId(data.data.request_id);
                setCooldown(getOtpCooldown(failure.response));
            }
            if (failure.response?.status === 429) setCooldown(getOtpCooldown(failure.response));
            const errors = Object.values(data?.errors || {}).flat();
            setError(errors.join(' ') || data?.message || failure.message || 'Unable to save your profile. Please try again.');
        } finally {
            pending.current = false;
            if (current()) setBusy(false);
        }
    };

    const sendCode = async () => {
        const response = await customerApi.requestEmailVerification(email.trim());
        if (!current()) return;
        const challenge = response.data?.data;
        if (!challenge?.request_id) throw new Error('Unable to send a verification code. Please try again.');
        setRequestId(challenge.request_id);
        setOtp('');
        setCooldown(getOtpCooldown(response));
    };

    const submit = event => {
        event.preventDefault();
        run(async () => {
            const [firstName = '', ...remainingNames] = fullName.trim().split(/\s+/);
            const lastName = remainingNames.join(' ');
            if (needsName && !firstName) throw new Error('Please enter your full name.');
            if (needsName && (firstName.length > 100 || lastName.length > 100)) throw new Error('Please use no more than 100 characters for each part of your name.');
            if (needsEmail && !emailVerified) {
                if (!requestId) {
                    if (cooldown) return;
                    await sendCode();
                    return;
                }
                await customerApi.verifyEmail({request_id:requestId, otp});
                if (!current()) return;
                setEmailVerified(true);
            }
            const payload = {};
            if (needsName) Object.assign(payload, {first_name:firstName.trim(), last_name:lastName.trim()});
            if (Object.keys(payload).length) await customerApi.updateProfile(payload);
            if (!current()) return;
            if (needsName) localStorage.setItem(USERNAME_KEY, [firstName.trim(), lastName.trim()].filter(Boolean).join(' '));
            onSaved();
        });
    };

    return <Modal show centered animation={false} onHide={onLater} backdrop={busy ? 'static' : true} keyboard={!busy}
        dialogClassName="nw-profile-modal" aria-labelledby="profile-setup-title" aria-describedby="profile-setup-description">
        <Modal.Header>
            <div className="nw-profile-modal__leaf"><BotanicalLeaf /></div>
            <button className="nw-profile-modal__close" type="button" aria-label="Close profile setup" disabled={busy} onClick={onLater}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18"/></svg>
            </button>
            <div className="nw-profile-modal__intro">
                <span className="nw-profile-modal__eyebrow">YOUR NATURE WAY ACCOUNT</span>
                <Modal.Title id="profile-setup-title">Complete your <span>profile.</span></Modal.Title>
                <p id="profile-setup-description">A few details for a more personal experience.</p>
            </div>
        </Modal.Header>
        <Modal.Body>
            {needsEmail && <ol className="nw-profile-modal__steps" aria-label="Profile setup progress">
                <li className={!requestId && !emailVerified ? 'is-current' : 'is-done'} aria-current={!requestId && !emailVerified ? 'step' : undefined}><span aria-hidden="true">{requestId || emailVerified ? '✓' : '1'}</span>Your details</li>
                <li className={requestId || emailVerified ? 'is-current' : ''} aria-current={requestId || emailVerified ? 'step' : undefined}><span aria-hidden="true">2</span>Verify email</li>
            </ol>}
            <form onSubmit={submit} aria-label="Complete your profile">
                {requestId && !emailVerified && <div className="nw-profile-modal__form-heading"><h3>Check your inbox</h3></div>}
                {needsName && <label className="nw-profile-modal__field">Full name<span className="nw-profile-modal__input"><FieldIcon /><input autoFocus required placeholder="Enter your full name" maxLength={201} autoComplete="name" value={fullName} disabled={busy} onChange={event => setFullName(event.target.value)} /></span></label>}
                {needsEmail && <>
                    <label className="nw-profile-modal__field">Email address<span className="nw-profile-modal__input"><FieldIcon email /><input required type="email" placeholder="you@example.com" maxLength={255} autoComplete="email" value={email} disabled={busy || !!requestId || emailVerified} onChange={event => setEmail(event.target.value)} /></span></label>
                    {!requestId && !emailVerified && <p className="nw-profile-modal__hint">We’ll send a code to verify your email address.</p>}
                    {requestId && !emailVerified && <div className="nw-profile-modal__verification">
                        <label className="nw-profile-modal__field">Email verification code<input className="nw-profile-modal__code" autoFocus required placeholder="000000" type="text" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} value={otp} disabled={busy} onChange={event => setOtp(event.target.value)} /></label>
                        <p className="nw-profile-modal__hint">Enter the 6-digit code sent to <strong>{email}</strong>.</p>
                        <div className="nw-profile-modal__code-actions">
                            <button className="nw-profile-modal__resend" type="button" disabled={busy || cooldown > 0} onClick={() => run(sendCode)}>{cooldown ? `Resend code in ${cooldown}s` : 'Resend code'}</button>
                            <button className="nw-profile-modal__resend" type="button" disabled={busy} onClick={() => {setRequestId(''); setOtp(''); setError('');}}>Use another email</button>
                        </div>
                    </div>}
                </>}
                {missing.has('mobile_verified') && <p>You can finish mobile verification on your profile page.</p>}
                {error && <p className="nw-profile-modal__error" role="alert">{error}</p>}
                <div className="nw-profile-modal__actions">
                    <button type="submit" disabled={busy || (needsEmail && !emailVerified && !requestId && cooldown > 0)}>{busy ? 'Please wait…' : requestId && !emailVerified ? 'Verify and continue' : 'Save and continue'}<span aria-hidden="true">→</span></button>
                    <button type="button" disabled={busy} onClick={onLater}>Complete later</button>
                </div>
            </form>
        </Modal.Body>
    </Modal>;
}
